import {ConsultationReferral} from '@/interface/common-interface';
import {Button, Checkbox, Icon, IconType, Modal, Text} from '@/ui-kits';
import {DetailItem} from '@/components';
import React, {useMemo, useState} from 'react';
import {ReferralType} from '@/constants/enums';
import {cn, formatName} from '@/utils/common_functions';
import {useSelector, useDispatch} from 'react-redux';
import {appSpecializations} from '@/services/redux/selectors';
import {referralConsultationRequest} from '@/services/redux/slices';
import {NA} from '@/constants';
interface ReferralModalProps {
  referral: ConsultationReferral;
  isOpen: boolean;
  onClose: () => void;
  specialistName: string;
  case_id: number;
  readonly: boolean;
}
export const ReferralModal: React.FC<ReferralModalProps> = props => {
  const {referral, isOpen, onClose, specialistName, case_id, readonly} = props;
  const dispatch = useDispatch();
  const specializations = useSelector(appSpecializations);
  const [shareDocuments, setShareDocuments] = useState<boolean | null>(null);
  const specializationLabel = useMemo(() => {
    if (typeof referral.data.specialization === 'number') {
      return specializations.find(specialization => specialization.id === referral.data.specialization)?.label;
    } else if (typeof referral.data.specialization === 'string') {
      return referral.data.specialization;
    } else {
      return NA;
    }
  }, [specializations, referral.data.specialization]);

  const handleBookNow = () => {
    if (shareDocuments === null) return;
    dispatch(referralConsultationRequest({id: case_id, allow_document_sharing: shareDocuments}));
    onClose();
  };

  const dataToMap = [
    {label: 'Name', value: formatName(referral.data.name || ''), icon: 'user2'},
    {label: 'Specialization', value: specializationLabel, icon: 'stethoscope2'},
    {label: 'Location', value: referral.data.location, icon: 'location'},
    {label: 'Contact Phone no.', value: referral.data.phone, icon: 'phone'},
    {label: 'Contact Email', value: referral.data.email, icon: 'phone', trucate: true},
    {label: 'Hospital name', value: referral.data.hospital, icon: 'phone'},
    {label: 'Address', value: referral.data.address, icon: 'home_plus'},
  ];
  return (
    <Modal
      open={isOpen}
      className={cn(
        'bg-white rounded-xl flex flex-col gap-2',
        referral.type === ReferralType.Internal ? 'sm:max-w-[400px] md:max-w-[500px] md:w-full p-4' : 'md:max-w-[600px] md:w-full p-6',
      )}>
      <header className="flex items-center justify-between">
        <Text className="text-ui_blue" variant="title">
          {referral.type === ReferralType.Internal ? 'Referral Details' : 'Referred to External Specialist'}
        </Text>
        <button onClick={onClose}>
          <Icon type="close" className="size-6 text-ui_blue " />
        </button>
      </header>

      {referral.type === ReferralType.Internal && (
        <>
          <div className="flex flex-col p-4 bg-[#F8FAFC] rounded-lg bg-ui_background_light border border-ui_input_border">
            <Text className="text-ui_blue text-center whitespace-pre-line !font-FigtreeMeduim" variant="body">
              {'You have been referred by'} <span className="font-FigtreeBold">Dr. {formatName(specialistName)} </span>
              to{' '}
              <span className="font-FigtreeBold">
                Dr. {formatName(referral.data.name || '')} ({specializationLabel})
                {referral.data.is_hospital_specialist && referral.data.hospital && (
                  <> <span className="font-FigtreeRegular">from</span> <span className="font-FigtreeBold">{referral.data.hospital}</span></>
                )}
              </span>
            </Text>
            <Text variant="subtitle" className="text-ui_blue self-center mt-4 !font-FigtreeSemiBold">
              Reason for refferal:
            </Text>
            <Text variant="body" className="text-ui_blue self-center text-center">
              {referral.data.reason}
            </Text>
            {!readonly && (
              <div className="flex flex-col gap-4 p-2 mt-4 rounded border-gray-400 border bg-white">
                <Text variant="small" className="text-center !font-FigtreeMeduim text-ui_blue text-wrap">
                  Do you agree to share your medical reports with the internally referred specialist?
                </Text>
                <div className="flex justify-between xs:px-2 md:px-4">
                  <Checkbox
                    checked={shareDocuments === true}
                    onChange={() => setShareDocuments(true)}
                    label={
                      <Text variant="small" className="!font-FigtreeSemiBold">
                        Yes, I agree
                      </Text>
                    }
                  />
                  <Checkbox
                    checked={shareDocuments === false}
                    onChange={() => setShareDocuments(false)}
                    label={
                      <Text variant="small" className="!font-FigtreeSemiBold">
                        No, I do not agree
                      </Text>
                    }
                  />
                </div>
              </div>
            )}
          </div>
          {!readonly && <Button title="Book Now" className="max-w-[220px] w-full self-center" disabled={shareDocuments === null} variant="primary" onClick={handleBookNow} />}
        </>
      )}

      {referral.type === ReferralType.External && (
        <>
          <Text variant="subtitle" className="text-ui_blue !font-FigtreeRegular">
            Dr. <span className="font-FigtreeBold">{formatName(specialistName)}</span> has referred you to an external specialist for further consultation.
          </Text>

          <Text variant="title" className="text-ui_blue">
            Specialist Details :
          </Text>
          <div className="grid lg:grid-cols-2 gap-2 grid-cols-1 h-fit">
            {dataToMap.map((item, index) => {
              if (!item.value) {
                return null;
              }
              return (
                <DetailItem
                  truncate={Boolean(item?.trucate)}
                  className={cn('border border-ui_input_border', index === dataToMap.length - 1 && 'col-span-full')}
                  label={item.label}
                  value={item.value}
                  icon={item.icon as IconType}
                />
              );
            })}
          </div>
          <div>
            <Text variant="title" className="text-ui_blue my-2">
              Reason for refferal:
            </Text>
            <div className="px-4 py-6 bg-[#F8FAFC] rounded-lg border border-ui_input_border">
              <Text className="text-ui_blue" variant="subtitle">
                {referral.data.reason}
              </Text>
            </div>
          </div>
          <Text variant="body" className="text-ui_blue mt-4">
            <span className="font-FigtreeBold">Note:</span> This consultation is outside our platform. You’ll need to contact the referred specialist directly
            to book your appointment.
          </Text>
        </>
      )}
    </Modal>
  );
};
